/* tslint:disable:no-console max-line-length disable-next-line */
import {FaultSyncQueuedItem} from '@docma-mm-core/fault/sync/fault-sync-queued-item';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {FaultPatch} from '@docma-mm-core/fault/patch/fault-patch';
import {SyncResult} from '@docma-mm-core/model/sync-state/sync-result';
import {User} from '@docma-mm-core/user/user';
import {FaultPatchService, faultUidsOfPatches} from '@docma-mm-core/fault/patch/fault-patch-service';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {HttpErrorResponse} from '@angular/common/http';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {FaultApiService} from '@docma-mm-core/fault/fault-api.service';
import {FaultPersistenceService} from '@docma-mm-core/fault/fault-persistence.service';
import {ProjectService} from '@mydocma/project';
import {MmProjectService} from '../../../mm-project/mm-project.service';
import {FaultOperationPathEnum} from '@docma-mm-core/fault/patch/fault-operation-path.enum';
import {GroupSyncService} from '@docma-mm-core/group/group-sync.service';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {FaultsEditData} from '@docma-mm-core/fault/faults-edit';
import {MmPlanEditService} from '../../../mm-faults/mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {ActivitySyncService} from '@docma-mm-core/activity/sync/activity-sync.service';
import {AttachmentSyncService} from '@docma-mm-core/attachment/attachment-sync.service';
import {Logger} from '@mydocma/common/src/lib/logger';
import {ErrorService} from '../../../mm-faults/mm-error.service';
import {Project} from '@docma-mm-core/project/project';
import {SyncStateChange} from '@docma-mm-core/fault/sync/fault-sync.service';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {ConnectivityService} from '@mydocma/core';
import {EventEmitter} from '@angular/core';

export class BaseFaultSyncService {

  constructor(protected readonly projectService: ProjectService,
              protected readonly mmProjectService: MmProjectService,
              protected readonly faultApiService: FaultApiService,
              protected readonly patchService: FaultPatchService,
              protected readonly persistenceService: FaultPersistenceService,
              protected readonly groupSyncService: GroupSyncService,
              protected readonly activitySyncService: ActivitySyncService,
              protected readonly attachmetSyncService: AttachmentSyncService,
              protected readonly planEditService: MmPlanEditService,
              protected readonly connectivityService: ConnectivityService,
              protected readonly logger: Logger,
              protected readonly errorService: ErrorService) {
  }

  protected syncingSubject = new BehaviorSubject<boolean>((false));
  protected syncingAllSubject = new BehaviorSubject<boolean>((false));

  public syncFaultsStateSubject = new Subject<{ changes: SyncStateChange[], startingFaultUids: string[], notifyError: boolean, modifiedOn?: Date, modifier?: User }>();
  public syncFaultsState$ = this.syncFaultsStateSubject.asObservable();

  private faultsDiscardLoadStateSubject = new Subject<string[]>();
  public onDiscardLoadState = this.faultsDiscardLoadStateSubject.asObservable();

  private lastSyncedFaultDataCache: Map<string, FaultData> = new Map();
  private readonly MAX_CACHE_SIZE = 100;

  protected autoSyncFinishedSubject = new BehaviorSubject<string[] | null>(null);
  public autoSyncFinished$ = this.autoSyncFinishedSubject.asObservable();

  private scribblesSyncedSubject = new EventEmitter<{attachments: string[]}>();
  public scribblesSyncedSubject$ = this.scribblesSyncedSubject.asObservable();

  public getLastSyncedFaultData(faultUid: string): FaultData | undefined {
    return this.lastSyncedFaultDataCache.get(faultUid);
  }

  async sync(syncingProject: Project, queuedFaults: { items: FaultSyncQueuedItem[], emit: boolean, startingFaultUids: string[], notifyError: boolean }) {

    const getQueuedFaults = () => {

      const errorGroups = queuedFaults.items.filter(x => errorFaultSyncQueuedItems.findIndex(f => f.faultUid === x.faultUid) !== -1 && x.groupUid)
        .reduce((arr: string[], item: FaultSyncQueuedItem) => arr.find(f => f === item.groupUid) ? arr : [...arr, item.groupUid], []);

      const filteredQueuedFaults = queuedFaults.items.filter(x =>
        errorFaultSyncQueuedItems.findIndex(f => f.faultUid === x.faultUid) === -1
        && errorFaultSyncQueuedItems.findIndex(f => f.id === x.id) === -1
        && errorGroups.findIndex(guid => x.groupUid === guid) === -1
        && finishedFaultSyncQueuedItems.findIndex(f => f.id === x.id) === -1
      );

      const sortedQueuedFaults = filteredQueuedFaults.sort((x, y) =>
        x.date.getTime() - y.date.getTime()
        || (!x.groupUid ? 0 : 1) - (!y.groupUid ? 0 : 1)
        || (!x.groupUid ?
          x.faultUid.localeCompare(y.faultUid) :
          x.groupUid.localeCompare(y.groupUid))
        || x.path.localeCompare(y.path)              // pathIndex in future !!!
      );

      return sortedQueuedFaults;
      return [];
    };

    const errorFaultSyncQueuedItems: FaultSyncQueuedItem[] = [];
    const finishedFaultSyncQueuedItems: FaultSyncQueuedItem[] = [];
    const faultSyncStateChanges: SyncStateChange[] = [];

    const syncResult: { items: FaultSyncQueuedItem[], lastAttemptOn: Date, error: string }[] = [];
    let modifiedOn: Date;
    let modifier: User;

    let sortedQueuedFaults = getQueuedFaults();

    if ((sortedQueuedFaults?.length ?? 0) > 0) {
      this.syncingSubject.next(true);
    }

    while ((sortedQueuedFaults?.length ?? 0) > 0) {

      const syncOn = new Date(Date.now());
      // let error: string;
      const firstItem = sortedQueuedFaults[0];
      const currentProject = await firstValueFrom(this.mmProjectService.currentProject);
      if (!syncingProject) {
        syncingProject = currentProject;
      }
      // const startedSyncs: Promise<SyncState>[] = [];


      if (!firstItem.groupUid || (!!firstItem.groupUid && (firstItem.path === FaultOperationPathEnum.attachmentScribbles || firstItem.path === FaultOperationPathEnum.activityAttachmentScribbles))) {
        let nextQueuedFaults: any;

        if (!!firstItem.groupUid && (firstItem.path === FaultOperationPathEnum.attachmentScribbles || firstItem.path === FaultOperationPathEnum.activityAttachmentScribbles)) {
          nextQueuedFaults = sortedQueuedFaults;
        } else {
          const f1 = sortedQueuedFaults.findIndex(f => f.groupUid != null);

          if (f1 === -1) {
            /* simple take all patches & group by faultUid */
            nextQueuedFaults = sortedQueuedFaults;
          } else {
            /*  take patches to (index - 1) & group by faultUid */
            nextQueuedFaults = sortedQueuedFaults.slice(0, f1);
          }
        }


        if (nextQueuedFaults && nextQueuedFaults.length > 0) {

          /* ### 1  */
          const groupedSyncItems = this.groupByFUid(nextQueuedFaults);

          await Promise.all(Object.keys(groupedSyncItems).map(async (faultUid) => {
            const patchesLoaded = await this.patchService.getByFaultUid(currentProject.MyDocmaUid, faultUid);
            const patches = patchesLoaded.filter(f => -1 !== groupedSyncItems[faultUid].findIndex(s => s.id === f.operation.id));
            const patchedFaultUids: string[] = faultUidsOfPatches(patches);

            console.debug('### exec queue.syncFaultFromPatches');
            const result = await this.syncFaultFromPatches(currentProject.MyDocmaUid, faultUid, groupedSyncItems[faultUid], patches);

            if (result?.syncState && result.syncState.status === SyncStatusEnum.Synced && !result.syncState.error) {
              Array.prototype.push.apply(finishedFaultSyncQueuedItems, groupedSyncItems[faultUid]);
              modifiedOn = result.modifiedOn;
              modifier = result.modifier;
            } else if (result?.syncState && result.syncState.status === SyncStatusEnum.Error) {
              Array.prototype.push.apply(errorFaultSyncQueuedItems, groupedSyncItems[faultUid]);
            }

            this.scribblesSyncedSubject.emit({attachments: [...(result.attachmentScribblesLoadStates?.map(m => m.data.ScribbleLayer.File.UniqueId) ?? []), ...(result.activityAttachmentScribblesLoadStates?.map(m => m.data.ScribbleLayer.File.UniqueId) ?? [])]});

            syncResult.push({items: groupedSyncItems[faultUid], lastAttemptOn: syncOn, error: (result.syncState?.error ? result.syncState.error : undefined)});

            /* validate change state from patches */
            // this.faultsSyncedSubject.next(patchedFaultUids);

            /* remove if needed */

            faultSyncStateChanges.push({faultUid, mydocmaUid: currentProject.MyDocmaUid, syncState: result?.syncState, faultId: (result.faultLoadStates && result.faultLoadStates.length > 0 ? result.faultLoadStates[0]?.data?.Id : undefined)});
          }));
        }
      } else {
        /* ### 2  */

        // take all of gUid
        const items = sortedQueuedFaults.filter(f => f.groupUid === firstItem.groupUid);
        const otherItems = sortedQueuedFaults.filter(f => f.groupUid === firstItem.groupUid && f.id !== firstItem.id);

        console.info('### start group sync ###');

        const patchesLoaded = await this.patchService.getByGroupUid(currentProject.MyDocmaUid, firstItem.groupUid);
        const patches = patchesLoaded.filter(f => -1 !== items.findIndex(s => s.id === f.operation.id));
        const patchedFaultUids: string[] = faultUidsOfPatches(patches);
        const additionalPatchedFaultUids: string[] = [];
        let result: SyncResult;

        const patchedGroupUids: string[] = [];
        const patchedAttachmentUids: string[] = [];

        if (items.every(x => x.path === FaultOperationPathEnum.group || x.path === FaultOperationPathEnum.groupActivity || x.path === FaultOperationPathEnum.groupAttachment)) {

          console.debug('### exec queue.syncGroupFromPatches');
          result = await this.groupSyncService.syncGroupFromPatches({
            mydocmaUid: currentProject.MyDocmaUid,
            projectId: currentProject.Id
          }, items, patches);

          patchedGroupUids.push(... result.groupLoadStates?.map(g => g.data.UniqueId));
          patchedAttachmentUids.push(... result.activityAttachmentsLoadStates?.map(att => att.data.UniqueId));

          if (result.syncState && result.syncState.status === SyncStatusEnum.Synced && !result.syncState.error) {
            result.activityLoadStates.forEach(al => {
              if (!items.some(s => s.faultUid === al.data?.Fault?.UniqueId)) {
                // finishedFaultSyncQueuedItems.push({mydocmaUid: currentProject.MyDocmaUid, date: firstItem.date, faultUid: al.data.Fault.UniqueId, groupUid: firstItem.groupUid, id: firstItem.id, op: firstItem.op, path: firstItem.path} as FaultSyncQueuedItem);
                additionalPatchedFaultUids.push(al.data?.Fault?.UniqueId);
              }
            });

            Array.prototype.push.apply(finishedFaultSyncQueuedItems, items);
            modifiedOn = result.modifiedOn;
            modifier = result.modifier;
            // this.removeItemsFromFaultQueue(items);
          } else if (result.syncState && result.syncState.status === SyncStatusEnum.Error) {
            Array.prototype.push.apply(errorFaultSyncQueuedItems, items);
          }

          syncResult.push({items, lastAttemptOn: syncOn, error: (result.syncState?.error ? result.syncState.error : undefined)});
        } else if (items.every(x => x.path === FaultOperationPathEnum.faults)) {
          /* ### 3  */

          console.debug('### exec queue.syncFaultEditFromPatches');
          const res = await this.syncFaultEditFromPatches({
            mydocmaUid: currentProject.MyDocmaUid,
            projectId: currentProject.Id
          }, items, patches);

          result = new SyncResult({syncState: res});

          if (result.syncState && result.syncState.status === SyncStatusEnum.Synced && !result.syncState.error) {
            Array.prototype.push.apply(finishedFaultSyncQueuedItems, items);
            // this.removeItemsFromFaultQueue(items);
          } else if (result.syncState && result.syncState.status === SyncStatusEnum.Error) {
            Array.prototype.push.apply(errorFaultSyncQueuedItems, items);
          }

          syncResult.push({items, lastAttemptOn: syncOn, error: (result.syncState?.error ? result.syncState.error : undefined)});

          const faultUids = items.map(m => m.faultUid);
          this.faultsDiscardLoadStateSubject.next(faultUids);
        }

        /* validate change state from patches */
        // this.faultsSyncedSubject.next(patchedFaultUids);

        /* remove if needed */
        Array.prototype.push.apply(faultSyncStateChanges, patchedGroupUids.map(groupUid => ({
          groupUid,
          mydocmaUid: currentProject.MyDocmaUid,
          syncState: result.syncState
        })));

        Array.prototype.push.apply(faultSyncStateChanges, patchedAttachmentUids.map(attachmentUid => ({
          attachmentUid,
          mydocmaUid: currentProject.MyDocmaUid,
          syncState: result.syncState
        })));

        Array.prototype.push.apply(faultSyncStateChanges, patchedFaultUids.map(faultUid => ({
          faultUid,
          mydocmaUid: currentProject.MyDocmaUid,
          syncState: result.syncState
        })));

        Array.prototype.push.apply(faultSyncStateChanges, additionalPatchedFaultUids.map(faultUid => ({
          faultUid,
          mydocmaUid: currentProject.MyDocmaUid,
          syncState: result.syncState
        })));


        console.info('### finished group sync ###');
      }


      sortedQueuedFaults = getQueuedFaults();
    }

    if ((faultSyncStateChanges?.length ?? 0) > 0) {
      /* SOMEHOW SYNC STATUS GOT LOST IN CASE: MULTI-ACTIVITY AFTER FAULT WITH SYNC ERROR */
      const allFaults = await this.persistenceService.getAll(syncingProject.MyDocmaUid, faultSyncStateChanges.filter(x => !!x.faultUid).map(m => m.faultUid));
      const allFaultsDict = Object.fromEntries(allFaults.map(x => [x.uid, x]));
      for (const faultSyncStateChange of faultSyncStateChanges) {
        if (allFaultsDict.hasOwnProperty(faultSyncStateChange.faultUid) && faultSyncStateChange.syncState.status !== allFaultsDict[faultSyncStateChange.faultUid].syncState.status) {
          this.persistenceService.saveSyncState(syncingProject.MyDocmaUid, faultSyncStateChange.faultUid, faultSyncStateChange.syncState);
        }
      }

      this.syncFaultsStateSubject.next({changes: faultSyncStateChanges, startingFaultUids: queuedFaults.startingFaultUids, notifyError: queuedFaults.notifyError, modifiedOn, modifier});
    }

    return {finishedFaultSyncQueuedItems, syncResult};

    // if ((finishedFaultSyncQueuedItems?.length ?? 0) > 0) {
    //   this.removeItemsFromFaultQueue(finishedFaultSyncQueuedItems, false);
    // }

    // this.mmPersonalizationService.setProjectValue(syncingProject.MyDocmaUid, PersonalizationKeysEnum.SyncResult, syncResult);
    // this.syncingSubject.next(false);

  }

  public async syncFaultFromPatches(mydocmaUid: string, faultUid: string, syncItems: FaultSyncQueuedItem[], patches: FaultPatch[]): Promise<SyncResult> {
    if (patches.length === 0) {
      return;
    }
    this.assumeIsOnline();

    let faultError: string;
    let childError: string;

    try {
      await this.persistenceService.saveSyncState(mydocmaUid, faultUid, new SyncState({status: SyncStatusEnum.Syncing, date: new Date(Date.now())}));

      const faultPatches = patches.filter(p => p.operation.path === FaultOperationPathEnum.fault);
      const faultChangeState = this.patchService.generateChangeStateFromPatches(faultPatches);
      let faultLoadState;
      let hasError = false;
      // let errorSyncState: SyncState;

      let hasChildError = false;
      const result: SyncResult = new SyncResult();
      let saveFaultLoadState = false;

      try {
        faultLoadState = await this.syncFaultDataFromPatches(mydocmaUid, faultUid, faultPatches);
        result.faultLoadStates.push(faultLoadState);
      } catch (err) {
        this.logger.error(err);

        faultError = this.errorService.getError(err);

        if (faultChangeState.status === ChangeStatusEnum.New) {
          throw err;
        }
        // Save sync errror and try to sync attachment/activities
        hasError = true;
      }

      const groupedAttachmentPatches = this.patchService.groupByOuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.attachment));
      const groupedAttachmentScribblePatches = this.patchService.groupByPuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.attachmentScribbles));

      for (const item of Object.entries(groupedAttachmentScribblePatches)) {
        // concat scribble patches with attachment patches
        groupedAttachmentPatches[item[0]] = (groupedAttachmentPatches[item[0]] ?? []).concat(item[1]);
      }

      for (const item of Object.values(groupedAttachmentPatches)) {
        const syncResult = await this.attachmetSyncService.syncFaultAttachmentFromPatches(mydocmaUid, (faultLoadState.data.Id ?? faultLoadState.data.uid), item);
        if (syncResult.syncState.status === SyncStatusEnum.Error) {
          childError = syncResult.syncState.error;
          hasChildError = true;
        } else {
          result.attachmentsLoadStates.push(...syncResult.attachmentsLoadStates);
          result.attachmentScribblesLoadStates.push(...syncResult.attachmentScribblesLoadStates);
        }
      }


      const groupedActivitiyPatches = this.patchService.groupByOuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.activity));
      const groupedActivityAttachmentPatches = this.patchService.groupByPuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachment));
      const groupedActivityAttachmentScribblePatches = this.patchService.groupByAuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachmentScribbles));

      for (const attachmentPatch of Object.entries(groupedActivityAttachmentPatches)) {
        groupedActivitiyPatches[attachmentPatch[0]] = (groupedActivitiyPatches[attachmentPatch[0]] ?? []).concat(attachmentPatch[1]);
      }

      for (const scribblePatches of Object.entries(groupedActivityAttachmentScribblePatches)) {
        groupedActivitiyPatches[scribblePatches[0]] = (groupedActivitiyPatches[scribblePatches[0]] ?? []).concat(scribblePatches[1]);
      }


      for (const item of Object.values(groupedActivitiyPatches)) {
        const syncResult = await this.activitySyncService.syncActivitiesFromPatches(mydocmaUid, faultLoadState?.data?.Id, item);

        if (syncResult.syncState.status === SyncStatusEnum.Error) {
          childError = syncResult.syncState.error;
          hasChildError = true;
        } else {
          result.activityLoadStates.push(...syncResult.activityLoadStates);
          result.activityAttachmentsLoadStates.push(...syncResult.activityAttachmentsLoadStates);
          result.activityAttachmentScribblesLoadStates.push(...syncResult.activityAttachmentScribblesLoadStates);
        }
      }

      await this.planEditService.addSyncedPinOnSelectedPlan(faultLoadState, faultUid);

      console.debug(`Sync fault ${faultUid}: Completed`, faultLoadState?.data?.Id);

      const res = modifiedOnOfSyncResult(result);
      saveFaultLoadState = res.saveFaultLoadState;
      if (saveFaultLoadState) {
        result.modifiedOn = res.modifiedOn;
        result.modifier = res.modifier;
      }

      // notify sync status change
      if (!hasError && !hasChildError) {
        if (saveFaultLoadState) {
          await this.persistenceService.saveLoadState(mydocmaUid, faultUid, result.faultLoadStates[0]);
        }

        if (result.faultLoadStates[0]?.data) {
          if (this.lastSyncedFaultDataCache.size >= this.MAX_CACHE_SIZE) {
            const firstKey = this.lastSyncedFaultDataCache.keys().next().value;
            this.lastSyncedFaultDataCache.delete(firstKey);
          }
          this.lastSyncedFaultDataCache.set(faultUid, result.faultLoadStates[0].data);
        }

        const syncState = await this.persistenceService.saveSyncState(mydocmaUid, faultUid, new SyncState({
          status: SyncStatusEnum.Synced,
          date: new Date(Date.now())
        }));
        return new SyncResult({
          syncState, faultLoadStates: result.faultLoadStates,
          activityLoadStates: result.activityLoadStates,
          attachmentsLoadStates: result.attachmentsLoadStates,
          attachmentScribblesLoadStates: result.attachmentScribblesLoadStates,
          activityAttachmentsLoadStates: result.activityAttachmentsLoadStates,
          activityAttachmentScribblesLoadStates: result.activityAttachmentScribblesLoadStates
        });

      } else if (hasError || hasChildError) {
        const syncStateErr = await this.persistenceService.saveSyncState(mydocmaUid, faultUid, new SyncState({
          status: SyncStatusEnum.Error,
          date: new Date(Date.now()),
          error: (faultError ?? childError)
        }));
        return new SyncResult({syncState: syncStateErr});
      }
      return new SyncResult({syncState: null});
    } catch (err) {
      console.debug(`Syc fault ${faultUid}: Error`);
      // notify sync status change
      const syncStateErr = await this.persistenceService.saveSyncState(mydocmaUid, faultUid, new SyncState({
        status: SyncStatusEnum.Error,
        date: new Date(Date.now()),
        error: faultError
      }));
      return new SyncResult({syncState: syncStateErr});
    }
  }

  private async syncFaultDataFromPatches(mydocmaUid: string, faultUid: string, faultPatches: FaultPatch[]): Promise<LoadState<FaultData>> {
    let faultId: any = faultPatches.length > 0 ? (faultPatches[0].oId ?? faultPatches[0].fUid) : -1;
    const faultChangeState = this.patchService.generateChangeStateFromPatches(faultPatches);

    let faultLoadedState = await this.persistenceService.getLoadState(mydocmaUid, faultUid);
    faultId = faultId && faultId === -1 ? (faultLoadedState?.data?.Id ?? faultLoadedState?.data?.UniqueId) : faultId;
    faultLoadedState = await this.syncFaultChangeState(faultId, faultChangeState, faultLoadedState);
    await this.patchService.remove(mydocmaUid, faultPatches.map(p => p.operation.id));

    await this.persistenceService.saveLoadState(mydocmaUid, faultUid, faultLoadedState);

    return faultLoadedState;
  }

  private async syncFaultChangeState(faultId: any, changeState: ChangeState<FaultData>,
                                     loadState?: LoadState<FaultData>): Promise<LoadState<FaultData>> {
    let savedFaultData: FaultData;
    if (changeState.status === ChangeStatusEnum.New) {
      const newFault = {...changeState.data} as any;

      if (newFault.Number2?.includes('#')) {
        newFault.Number2Prefix = newFault.Number2?.split('#')[0] ?? '';
        newFault.Number2Suffix = newFault.Number2?.split('#')[1] ?? '';
        if (newFault.Number2Prefix || newFault.Number2Suffix) {
          delete newFault.Number2;
        }
      }
      try {
        savedFaultData = await this.faultApiService.createFault(newFault).toPromise();
      } catch (err) {
        this.logger.error(err, '', 'BaseFaultSyncService');
        throw err;
      }
      savedFaultData.Files = null;
      savedFaultData.Activities = null;
      faultId = savedFaultData.Id ?? savedFaultData.UniqueId;
    } else {
      if (changeState.status === ChangeStatusEnum.Changed) {
        const changes = {...changeState.data} as any;
        if (loadState?.data?.ModifiedOn) {
          changes.ModifiedOn = loadState.data.ModifiedOn;
        } else {
          changes.ModifiedOn = changeState.date;
        }


        if (changes.Number2?.includes('#')) {
          changes.Number2Prefix = changes.Number2?.split('#')[0] ?? '';
          changes.Number2Suffix = changes.Number2?.split('#')[1] ?? '';
          if (changes.Number2Prefix || changes.Number2Suffix) {
            delete changes.Number2;
          }
        }

        try {
          savedFaultData = await this.faultApiService.updateFault(faultId, changes).toPromise();
        } catch (err) {
          if (err instanceof HttpErrorResponse) {
            const errorResponse = err as HttpErrorResponse;
            // CONFLICT => Refresh modify date and try again
            if (err.status === 409 && err.error?.ModifiedOn) {
              changes.ModifiedOn = err.error.ModifiedOn;
              savedFaultData = await this.faultApiService.updateFault(faultId, changes).toPromise();
            } else {
              throw err;
            }

          } else {
            throw err;
          }
        }
      } else {
        return loadState;
      }
      // Load full fault data inclusive attachments, activities again
      // const founds = await this.faultApiService.searchFaults(0, 0, 1, 0, 0, 0, 0, [], [faultId]).toPromise();
      // if (founds && founds.Data && founds.Data.length === 1) {
      //   savedFaultData = founds.Data[0];
      // }

    }
    return new LoadState({status: LoadStatusEnum.Loaded, data: savedFaultData});
  }

  public async syncFaultEditFromPatches(project: { mydocmaUid: string, projectId: number },
                                        syncItems: FaultSyncQueuedItem[],
                                        patches: FaultPatch[]): Promise<SyncState> {
    let groupUid;

    try {
      const gUids = new Set(syncItems.map(x => x.groupUid));
      if (gUids.size > 1) {
        throw new Error('Only sync items of one gUid could be passed at once!');
      }

      console.info('### syncFaultEditFromPatches 01');

      groupUid = [...gUids][0];

      if (patches.length === 0 || patches.length !== syncItems.length) {
        return;
      }

      if (patches.every(e => e.operation.path === patches[0].operation.path
        && e.operation.op === patches[0].operation.op
        && e.operation === patches[0].operation)) {
        throw new Error('Only sync items with exact same change could be passed!');
      }

      const faultEditPatches = patches.filter(f => f.operation.path === FaultOperationPathEnum.faults);
      const groupedFaultEditPatches = groupFaults(faultEditPatches);
      const syncingFaultUids: string[] = [];

      for (const key of Object.keys(groupedFaultEditPatches)) {
        const patchesInternal = groupedFaultEditPatches[key] as FaultPatch[];

        if (syncingFaultUids.findIndex(x => x === patchesInternal[0].fUid) === -1) {
          syncingFaultUids.push(patchesInternal[0].fUid);
        }
      }

      console.info('### syncFaultEditFromPatches 02');

      await this.persistenceService.saveSyncStates(project.mydocmaUid,
        syncingFaultUids,
        new SyncState({status: SyncStatusEnum.Syncing, date: new Date(Date.now())}));

      console.info('### syncFaultEditFromPatches 03');

      let faultsLoadStates: LoadState<FaultData>[];
      const hasFaultError = false;

      try {
        if (faultEditPatches && faultEditPatches.length > 0) {
          faultsLoadStates = await this.syncFaultEditDataFromPatches(project, faultEditPatches);
        }
      } catch (err) {
        throw err;

        // if (activityChangeState.status === ChangeStatusEnum.New) {
        // }
        // // Save sync errror and try to sync attachment
        // await this.activityDbService.saveSyncState(project.mydocmaUid, faultUid, activityUid, new SyncState({ status: SyncStatusEnum.Error, date: new Date(Date.now()) }));
        //   hasActivityError = true;
      }

      console.info('### syncFaultEditFromPatches 04');


      await this.persistenceService.saveLoadStates(project.mydocmaUid, syncingFaultUids, faultsLoadStates);

      await this.persistenceService.saveSyncStates(project.mydocmaUid,
        syncingFaultUids,
        new SyncState({status: SyncStatusEnum.Synced, date: new Date(Date.now())}));

      return new SyncState({status: SyncStatusEnum.Synced, date: new Date(Date.now())});
    } catch (err) {
      const errSyncState = new SyncState({status: SyncStatusEnum.Error, date: new Date(Date.now())});
      await this.persistenceService.saveSyncStates(project.mydocmaUid, [], errSyncState);
      return errSyncState;
    }
  }

  private async syncFaultEditDataFromPatches(project: { mydocmaUid: string, projectId: number },
                                             patches: FaultPatch[]): Promise<LoadState<FaultData>[]> {

    // this.assertValidPatches(patches, FaultOperationPathEnum.faults);
    let loadStates: LoadState<FaultData>[];
    const changeStates: ChangeState<FaultData>[] = [];

    const groupedFaultEditPatches = groupFaults(patches);

    const faultIds = Object.keys(groupedFaultEditPatches).map(key => {
      const patchesInternal = groupedFaultEditPatches[key] as FaultPatch[];
      const changeStateInternal = this.patchService.generateChangeStateFromPatches(patchesInternal);
      changeStates.push(changeStateInternal);
      return patchesInternal[0].oId;
    });

    loadStates = await this.persistenceService.getLoadStates(project.mydocmaUid, patches.map(x => x.fUid));

    if (changeStates.filter(x => x.status !== ChangeStatusEnum.NotChanged).length > 0) {
      await this.syncFaultsEditChangeState(project,
        faultIds,
        changeStates.filter(x => x.status !== ChangeStatusEnum.NotChanged));

      // const loadedStatesParam = loadedStates.map(x => ({ activityUid: x.data.UniqueId, faultUid: faultMap.get(x.data.UniqueId), loadState: x}));
      // await this.persistenceService.saveLoadState(project.mydocmaUid, loadedStatesParam);
    }

    loadStates.forEach(ls => {
      const changeState = changeStates[0];
      Object.keys(changeState.data).forEach(key => {
        ls.data[key] = changeState.data[key];
      });
    });

    const comittedOperations = patches.map(p => p.operation.id);
    await this.patchService.remove(project.mydocmaUid, comittedOperations);

    return loadStates;
  }

  private async syncFaultsEditChangeState(project: { mydocmaUid: string, projectId: number },
                                          faultIds: number[],
                                          changeStates: ChangeState<any>[]) {

    const changedChangeStates = changeStates.filter(x => x.status === ChangeStatusEnum.Changed);

    if (changedChangeStates.length > 0) {
      const changedFaults = changedChangeStates.map(x => x.data);
      const faultsEditData = new FaultsEditData(changedFaults, faultIds);

      console.info('### syncFaultsEditChangeState ###');

      const saveResponse = await this.faultApiService.patchFaults(project, faultsEditData).toPromise();
      // saveResponse.activities = changedActivities;

      console.info('### finished syncFaultsEditChangeState ###');

      // const loadStates: LoadState<FaultData>[] = [];
      // saveResponse.activities.forEach(a => loadStates.push(new LoadState<FaultData>({ status: LoadStatusEnum.Loaded, data: a})));

      return null;
    }
  }

  public groupByFUid(syncQueuedItems: Array<FaultSyncQueuedItem>): { [fuid: string]: Array<FaultSyncQueuedItem> } {
    return syncQueuedItems
      .sort((prev, curr) => +prev?.date - +curr?.date)
      .reduce((acc, curr) => {
        const faultUid = curr.faultUid;

        if (acc[faultUid] == null) {
          acc[faultUid] = [];
        }

        acc[faultUid].push(curr);

        return acc;
      }, {});
  }

  private assumeIsOnline() {
    if (this.connectivityService.isOfflineVal) {
      throw new Error(`Can't sync in offline`);
    }
  }
}

export function modifiedOnOfSyncResult(result: SyncResult): { saveFaultLoadState: boolean, modifiedOn: Date, modifier: User } {
  let saveFaultLoadState = false;
  let modifiedOn: Date;
  let modifier: User;

  try {
    const maxAttachment = ((result?.attachmentsLoadStates?.length ?? 0) > 0 ?
      result.attachmentsLoadStates.reduce((prev, current) =>
        (prev && prev.data.ModifiedOn > current.data.ModifiedOn) ? prev : current) : undefined);

    const maxActivity = ((result?.activityLoadStates?.length ?? 0) > 0 ?
      result.activityLoadStates.reduce((prev, current) =>
        (prev && prev.data.ModifiedOn > current.data.ModifiedOn) ? prev : current) : undefined);

    const maxActivityAttachment = ((result?.activityAttachmentsLoadStates?.length ?? 0) > 0 ?
      result.activityAttachmentsLoadStates.reduce((prev, current) =>
        (prev && prev.data.ModifiedOn > current.data.ModifiedOn) ? prev : current) : undefined);

    result.faultLoadStates.forEach(faultLoadState => {

      if (maxAttachment && (!faultLoadState.data?.ModifiedOn || faultLoadState.data?.ModifiedOn < new Date(maxAttachment.data.ModifiedOn))) {
        if (faultLoadState?.data && (faultLoadState?.status ?? LoadStatusEnum.NotLoaded) !== LoadStatusEnum.NotLoaded) {
          faultLoadState.data.ModifiedOn = maxAttachment.data.ModifiedOn;
          faultLoadState.data.Modifier = maxAttachment.data.Modifier;
        }
        modifiedOn = maxAttachment.data.ModifiedOn;
        modifier = maxAttachment.data.Modifier;
        saveFaultLoadState = true;
      }

      if (maxActivity && (!faultLoadState.data?.ModifiedOn || faultLoadState.data?.ModifiedOn < new Date(maxActivity.data.ModifiedOn))) {
        if (faultLoadState.data && (faultLoadState?.status ?? LoadStatusEnum.NotLoaded) !== LoadStatusEnum.NotLoaded) {
          faultLoadState.data.ModifiedOn = maxActivity.data.ModifiedOn;
          faultLoadState.data.Modifier = maxActivity.data.Modifier;
        }
        modifiedOn = maxActivity.data.ModifiedOn;
        modifier = maxActivity.data.Modifier;
        saveFaultLoadState = true;
      }

      if (maxActivityAttachment && (!faultLoadState.data?.ModifiedOn || faultLoadState.data?.ModifiedOn < new Date(maxActivityAttachment.data.ModifiedOn))) {
        if (faultLoadState.data && (faultLoadState?.status ?? LoadStatusEnum.NotLoaded) !== LoadStatusEnum.NotLoaded) {
          faultLoadState.data.ModifiedOn = new Date(maxActivityAttachment.data.ModifiedOn);
          faultLoadState.data.Modifier = maxActivityAttachment.data.Modifier;
        }
        modifiedOn = maxActivityAttachment.data.ModifiedOn;
        modifier = maxActivityAttachment.data.Modifier;
        saveFaultLoadState = true;
      }

    });
  } catch (err) {
    // standalone function - no dependency injection available, Logger cannot be injected here
    console.error('Error gettting max modifiedOn/modifier.', err);
  }
  return {saveFaultLoadState, modifiedOn, modifier};
}

function groupFaults(faultPatches: FaultPatch[]) {
  return faultPatches.reduce((acc, curr) => {
    if (curr.oId) {
      const key = curr.oId;
      if (acc[key] == null) {
        acc[key] = [];
      }
      acc[key].push(curr);
    }
    return acc;
  }, {});
}
